import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { ShieldCheck, ExternalLink, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export function ConsentGate({ children }) {
  const [checked, setChecked] = useState(false);
  const [pending, setPending] = useState(null); // null = loading, [] = ok
  const [agree, setAgree] = useState(false);
  const [marketing, setMarketing] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/me/consents')
      .then(r => setPending(r.data.needs_reconsent ? r.data.pending_policies : []))
      .catch(() => setPending([]))
      .finally(() => setChecked(true));
  }, []);

  const accept = async () => {
    setSaving(true);
    try {
      await api.post('/me/accept-policies', { marketing });
      setPending([]);
      toast.success('Thanks — your preferences have been saved.');
    } catch {
      toast.error('Could not save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (!checked) return children;
  if (!pending || pending.length === 0) return children;

  return (
    <>
      {children}
      <div className="fixed inset-0 z-[100] bg-[var(--c-bg)]/95 backdrop-blur-md flex items-center justify-center p-4 overflow-y-auto" data-testid="consent-gate">
        <div className="regen-card-elevated max-w-md w-full p-6 my-8">
          <div className="flex items-center gap-2 mb-1">
            <ShieldCheck className="w-5 h-5 text-[#00C8FF]" />
            <span className="label-mini text-[#00C8FF]">Action required</span>
          </div>
          <h2 className="font-display font-black text-2xl tracking-tight">We've updated our policies</h2>
          <p className="text-sm text-muted-foreground mt-2">
            To keep using YourReGen, please review and accept the latest versions of the documents below.
          </p>

          <div className="mt-4 space-y-2">
            {pending.map(p => (
              <a key={p.slug} href={`/legal/${p.slug}`} target="_blank" rel="noreferrer"
                 data-testid={`consent-link-${p.slug}`}
                 className="flex items-center justify-between rounded-xl bg-[var(--c-film)] px-4 py-3 hover:bg-[var(--c-film)] transition-colors">
                <span className="text-sm font-medium">{p.title}</span>
                <span className="flex items-center gap-1.5 text-xs text-[#00C8FF]">v{p.version} <ExternalLink className="w-3.5 h-3.5" /></span>
              </a>
            ))}
          </div>

          <label className="flex items-start gap-3 mt-5 cursor-pointer" data-testid="consent-agree-row">
            <Checkbox checked={agree} onCheckedChange={setAgree} data-testid="consent-agree" className="mt-0.5" />
            <span className="text-sm text-foreground/90 leading-snug">
              I have read and accept the documents above, including the processing of my health & fitness data to provide coaching.
            </span>
          </label>
          <label className="flex items-start gap-3 mt-3 cursor-pointer" data-testid="consent-marketing-row">
            <Checkbox checked={marketing} onCheckedChange={setMarketing} data-testid="consent-marketing" className="mt-0.5" />
            <span className="text-sm text-muted-foreground leading-snug">
              (Optional) Send me tips, offers and product updates by email.
            </span>
          </label>

          <Button onClick={accept} disabled={!agree || saving} data-testid="consent-accept-btn"
                  className="w-full mt-5 h-12 bg-[#00C8FF] text-[#081726] font-bold rounded-xl disabled:opacity-40">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Accept & Continue'}
          </Button>
        </div>
      </div>
    </>
  );
}
